import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import { getClinicId } from '../../../../utils/config';
import FormatUrl from '../../../../utils/UrlFormatter';
import LoadingSpinner from '../../../common/LoadingSpinner';
import LegendHorizontal from './LegendHorizontal';

import './PatientIntakeForm.css';

const PatientIntakeForm = ({user}) => {
  
  const navigate = useNavigate();
  const [medicalHistory, setMedicalHistory] = useState({
    personal_info: {
      name: '', date: '', address: '', city: '', province:'', postal_code: '', home_phone: '',
      work_phone: '', occupation: '', date_of_birth: '', email: '', height: '',
      weight: '', doctor: '', doctor_phone: '', may_contact: '',
      emergency_contact_name: '', emergency_contact_phone: '', had_massage_before: '',
      massage_reason: '', current_medications: '', previous_illnesses: '',
      accidents: '', other_conditions: '', family_history: ''
    },

    joint_soft_tissue: {
        arms: '', upper_back: '', mid_back: '', lower_back: '',
        degenerative_discs: '', feet: '', hands: '', hips: '', jaw: '',
        knees: '', legs: '', neck: '', osteo_arthritis: '',
        rheumatoid_arthritis: '', sciatica: '', shoulders: '',
        limitation_of_movement: '', joint_notes: '', other: ''
    },

    skin: {
    rashes: '', itching: '', bruise_easily: '',
    dryness: '', boils: '', other: ''
    },

    general_symptoms: {
    fainting: '', dizziness: '', loss_of_sleep: '',
    fatigue: '', nervousness: '', weight_change: '',
    numbness: '', tingling: '', paralysis: '',
    tension_headaches: '', migraines: ''
    },
      
    cardiovascular: {
    high_bp: '', low_bp: '', coronary_disease: '',
    heart_attack: '', phlebitis: '', stroke: '', pacemaker: '',
    heart_murmur: '', palpitations: '', varicose_veins: '',
    ankle_swelling: '', poor_circulation: ''
    },

    infectious: {
    hepatitis: '', tuberculosis: '', hiv: '', herpes: '',
    cold: '', flu: '', athletes_foot: '', warts: '', other: ''
    },

    digestive: {
    poor_appetite: '', gas: '', constipation: '', diarrhea: '',
    nausea: '', ulcer: '', vomiting: ''
    },

    eent: {
    allergies: '', frequent_colds: '', glasses_contacts: '',
    hearing_aid: '', hearing_loss: '', sinus_infection: '', swollen_glands: ''
    },

    reproductive: {
    pregnant: '', due_date: '', painful_menstruation: '', heavy_flow: '',
    irregular_cycle: '', swollen_breasts: '', menopausal: '',
    pre_menopausal: '', post_menopausal: '', birth_control: '', birth_control_type: ''
    },

    respiratory: {
    chronic_cough: '', bronchitis: '', asthma: '', hay_fever: '',
    difficulty_breathing: '', smoking: '', emphysema: '', pneumonia: ''
    },
      
    lifestyle: {
        eating_habits: '', takes_vitamins: '', vitamin_type: '', vitamin_frequency: '',
        regular_exercise: '', exercise_type: '', exercise_frequency: '',
        energy_level: '', stress: '', stress_type: '', computer_use: '', hours_per_day: ''
    },

    consent: {
    signature: '', consent_date: ''
    }
    
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    setMedicalHistory((prev) => {
      if (!prev.personal_info.date) {
        const today = new Date().toISOString().split("T")[0]; // format: YYYY-MM-DD
        return {
          ...prev,
          personal_info: {
            ...prev.personal_info,
            date: today,
          },
        };
      }
      return prev;
    });
  }, []);
  
  const validateForm = () => {
    if (!medicalHistory?.personal_info) return false;

    const newErrors = {};
    const { personal_info } = medicalHistory;
  
    if (!personal_info.name?.trim()) newErrors.name = 'Name is required';
    if (!personal_info.date?.trim()) newErrors.date = 'Date is required';
    if (!personal_info.date_of_birth?.trim()) newErrors.date_of_birth = 'DOB is required';
    if (!personal_info.address?.trim()) newErrors.address = 'Address is required';
    if (!personal_info.work_phone?.trim()) newErrors.work_phone = 'Phone number is required';
    if (!personal_info.email?.trim()) newErrors.email = 'Email is required';
    if (!personal_info.had_massage_before?.trim()) newErrors.had_massage_before = 'Please select an option';
    if (!personal_info.massage_reason?.trim()) newErrors.massage_reason = 'Please specify reason';
    if (!personal_info.city?.trim()) newErrors.city = 'City is required';
    if (!personal_info.province?.trim()) newErrors.province = 'Province is required';
    if (!personal_info.postal_code?.trim()) newErrors.postal_code = 'Postal Code is required';
    if (!medicalHistory.consent?.signature?.trim()) {
      newErrors.signature = 'Signature is required';
    }
    if (!medicalHistory.consent?.consent_date?.trim()) {
      newErrors.consent_date = 'Date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  
  const handleChange = (section, field, value) => {
    setMedicalHistory(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };


  useEffect(() => {
    if (user?.patient_id) {
      localStorage.setItem("patient_id", user.patient_id);
    }
  }, [user]);
  
  const getPatientId = useCallback(() => {
    const patientId = user?.patient_id || localStorage.getItem("patient_id");
    return patientId;
  }, [user]);
  
  
  const fetchExistingIntakeForm = useCallback(async () => {
    try {
      const clinic_id = getClinicId(user);
      const patient_id = getPatientId();

      const queryParams = new URLSearchParams({
        clinic_id,
        patient_id,
      });
  
      const response = await fetch(FormatUrl(`/get-patient-intake-form/?${queryParams}`), {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
  
      const data = await response.json();
      if (data.status === "Success" && data.medical_history) {
        const parsed = JSON.parse(data.medical_history);
        // setMedicalHistory(parsed || {});
        setMedicalHistory(prev => ({
          ...prev,
          ...parsed,
          personal_info: {
            ...prev.personal_info,
            ...(parsed.personal_info || {})
          }
        }));
        
      }
    } catch (error) {
      console.error("Error fetching intake form:", error);
    }
  }, [user, getPatientId]);
  
  useEffect(() => {
    fetchExistingIntakeForm();
  }, [fetchExistingIntakeForm]);
  
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const clinic_id = getClinicId(user);
      // const patient_id = user?.patient_id;
      const medical_history = JSON.stringify(medicalHistory);
  
      // Fetch CSRF Token
      const csrfRes = await fetch(FormatUrl("/csrf-token/"), {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
  
      const { csrfToken } = await csrfRes.json();
  
      const formData = new FormData();
      formData.append("clinic_id", clinic_id);
      formData.append("patient_id", getPatientId());
      formData.append("medical_history", medical_history);
  
      // console.log("Submitted Payload:", {
      //   clinic_id,
      //   patient_id,
      //   medical_history: JSON.parse(medical_history), 
      // });
  
      const response = await fetch(FormatUrl("/add-patient-intake-form/"), {
        method: "POST",
        headers: {
          "X-CSRFToken": csrfToken,
        },
        credentials: "include",
        body: formData,
      });
  
      const result = await response.json();
  
      if (result.status === "Success") {
        toast.success("Form submitted successfully!");
        setTimeout(() => {
          navigate("/patient-appointments");
        }, 3000);
      } else {
        toast.error("Submission failed. Please try again.");
        console.error("Backend Error:", result);
      }
    } catch (error) {
      toast.error("An unexpected error occurred.");
      console.error("Submission Error:", error);
    }
  };
  
  const [loading, setLoading] = useState(true);
  const [hasAppointment, setHasAppointment] = useState(false);
  // const [appointments, setAppointments] = useState([]);

useEffect(() => {
  const fetchAppointments = async () => {
    const patient_id = getPatientId();
    if (!patient_id) {
      setHasAppointment(false);
      return;
    }

    try {
      // Fetch CSRF Token
      const csrfUrl = FormatUrl(`/csrf-token/`);
      const csrfResponse = await fetch(csrfUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const csrfData = await csrfResponse.json();
      const csrfToken = csrfData.csrfToken;

      const apiUrl = FormatUrl(`/get-patient-appointments/?patient_id=${patient_id}`);
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
      });

      const data = await response.json();
      const filtered = (data.appointments || []).filter(app => app.status !== 'CA');

      // setAppointments(filtered);
      setHasAppointment(filtered.length > 0);

    } catch (error) {
      console.error("Error fetching appointments:", error);
      setHasAppointment(false);
    } finally{
      setLoading(false);
    }
  };

  fetchAppointments();
}, [getPatientId]);

if (loading) {
  return <LoadingSpinner />; 
}

  return (
    hasAppointment ? (
      <form className="patient-intake-form" onSubmit={handleSubmit}>
      <h2>Patient Health History Form</h2>

      {/* <div className="intake-radio-legend-horizontal">
      <h4 className="legend-title-horizontal">What N / P / C Mean</h4>
        <div className="legend-items-horizontal">
          <span className="legend-item">
            <strong className="legend-label">N</strong> - No
          </span>
          <span className="legend-item">
            <strong className="legend-label">P</strong> - Past
          </span>
          <span className="legend-item">
            <strong className="legend-label">C</strong> - Current
          </span>
        </div>
      </div> */}
    <fieldset className="intake-section-intake-form personal-info-section-intake-form">
    <legend className="intake-legend">Personal Information</legend>

    <div className="form-group-intake two-column-intake">
    <label className="intake-label-inline">
      Full Name<span className="mandatory-asterisk">*</span>
    </label>
      <input
        type="text"
        placeholder="Full Name"
        className="input-intake-form"
        value={medicalHistory?.personal_info?.name || ''}
        onChange={(e) => handleChange('personal_info', 'name', e.target.value)}
      />
      {errors.name && <p className="error">{errors.name}</p>}
    </div>

    <div className="date-dual-row-intake" style={{ marginBottom: '0.5rem' }}>
      <div className="date-field-wrapper-intake">
      <label className="date-inline-label-intake">
        Today's Date<span className="mandatory-asterisk">*</span>
      </label>
        <input
          type="date"
          className="input-intake-form"
          value={medicalHistory?.personal_info?.date || ''}
          onChange={(e) => handleChange('personal_info', 'date', e.target.value)}
        />
      {errors.date && <p className="error">{errors.date}</p>}
      </div>

      <div className="date-field-wrapper-intake">
        <label className="date-inline-label-intake">
          Date of Birth<span className="mandatory-asterisk">*</span>
        </label>
        <input
          type="date"
          className="input-intake-form"
          value={medicalHistory?.personal_info?.date_of_birth || ''}
          onChange={(e) => handleChange('personal_info', 'date_of_birth', e.target.value)}
        />
      {errors.date_of_birth && <p className="error">{errors.date_of_birth}</p>}
      </div>
    </div>

    <div className="form-group-intake">
      <label className="intake-label-inline">
        Address<span className="mandatory-asterisk">*</span>
      </label>
      <input
        type="text"
        placeholder="Address"
        className="input-intake-form"
        value={medicalHistory?.personal_info?.address || ''}
        onChange={(e) => handleChange('personal_info', 'address', e.target.value)}
      />
      {errors.address && <p className="error">{errors.address}</p>}
    </div>

    <div className="form-group-intake three-column-intake">
      <div>
        <label className="intake-label-inline">
          City<span className="mandatory-asterisk">*</span>
        </label>
        <input
          type="text"
          placeholder="City"
          className="input-intake-form"
          value={medicalHistory?.personal_info?.city || ''}
          onChange={(e) => handleChange('personal_info', 'city', e.target.value)}
        />
        {errors.city && <p className="error">{errors.city}</p>}
      </div>
      <div>
        <label className="intake-label-inline">
          Province<span className="mandatory-asterisk">*</span>
        </label>
        <input
          type="text"
          placeholder="Province"
          className="input-intake-form"
          value={medicalHistory?.personal_info?.province || ''}
          onChange={(e) => handleChange('personal_info', 'province', e.target.value)}
        />
        {errors.province && <p className="error">{errors.province}</p>}
      </div>
      <div>
        <label className="intake-label-inline">
          Postal Code<span className="mandatory-asterisk">*</span>
        </label>
        <input
          type="text"
          placeholder="Postal Code"
          className="input-intake-form"
          value={medicalHistory?.personal_info?.postal_code || ''}
          onChange={(e) => handleChange('personal_info', 'postal_code', e.target.value)}
        />
        {errors.postal_code && <p className="error">{errors.postal_code}</p>}
      </div>

      <div>
        <label className="intake-label-inline">
          Home Phone
        </label>
        <input
        type="text"
        placeholder="Home Phone"
        className="input-intake-form"
        value={medicalHistory?.personal_info?.home_phone || ''}
        onChange={(e) => handleChange('personal_info', 'home_phone', e.target.value)}
      />
        
      </div>
      
    </div>


    <div className="form-group-intake two-column-intake">
    <label className="intake-label-inline">
      Phone<span className="mandatory-asterisk">*</span>
    </label>
      <input
        type="text"
        placeholder="Phone"
        className="input-intake-form"
        value={medicalHistory?.personal_info?.work_phone || ''}
        onChange={(e) => handleChange('personal_info', 'work_phone', e.target.value)}
      />
      {errors.work_phone && <p className="error">{errors.work_phone}</p>}
      <input
        type="text"
        placeholder="Occupation"
        className="input-intake-form"
        value={medicalHistory?.personal_info?.occupation || ''}
        onChange={(e) => handleChange('personal_info', 'occupation', e.target.value)}
      />
    </div>

    <div className="form-group-intake three-column-intake">
    <label className="intake-label-inline">
      Email<span className="mandatory-asterisk">*</span>
    </label>
      <input
        type="email"
        placeholder="Email"
        className="input-intake-form"
        value={medicalHistory?.personal_info?.email || ''}
        onChange={(e) => handleChange('personal_info', 'email', e.target.value)}
      />
      <input
        type="text"
        placeholder="Height"
        className="input-intake-form"
        value={medicalHistory?.personal_info?.height || ''}
        onChange={(e) => handleChange('personal_info', 'height', e.target.value)}
      />
      {errors.email && <p className="error">{errors.email}</p>}
    </div>

    <div className="form-group-intake two-column-intake">
      <input
        type="text"
        placeholder="Weight"
        className="input-intake-form"
        value={medicalHistory?.personal_info?.weight || ''}
        onChange={(e) => handleChange('personal_info', 'weight', e.target.value)}
      />
      <input
        type="text"
        placeholder="Family Doctor"
        className="input-intake-form"
        value={medicalHistory?.personal_info?.doctor || ''}
        onChange={(e) => handleChange('personal_info', 'doctor', e.target.value)}
      />
    </div>

    <div className="form-group-intake">
      <input
        type="text"
        placeholder="Doctor Phone"
        className="input-intake-form"
        value={medicalHistory?.personal_info?.doctor_phone || ''}
        onChange={(e) => handleChange('personal_info', 'doctor_phone', e.target.value)}
      />
    </div>

    <div className="form-group-intake">
      <label className="intake-label-inline">May I contact ?</label>
      <select
        className="select-intake-form"
        value={medicalHistory?.personal_info?.may_contact || ''}
        onChange={(e) => handleChange('personal_info', 'may_contact', e.target.value)}
      >
        <option value="">Select</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    </div>

    <div className="form-group-intake two-column-intake">
      <input
        type="text"
        placeholder="Emergency Contact Name"
        className="input-intake-form"
        value={medicalHistory?.personal_info?.emergency_contact_name || ''}
        onChange={(e) => handleChange('personal_info', 'emergency_contact_name', e.target.value)}
      />
      <input
        type="text"
        placeholder="Emergency Contact Phone"
        className="input-intake-form"
        value={medicalHistory?.personal_info?.emergency_contact_phone || ''}
        onChange={(e) => handleChange('personal_info', 'emergency_contact_phone', e.target.value)}
      />
    </div>

    <div className="form-group-intake">
      <label className="intake-label-inline">Have you had massage before?<span className="mandatory-asterisk">*</span></label>
      <select
        className="select-intake-form"
        value={medicalHistory?.personal_info?.had_massage_before || ''}
        onChange={(e) => handleChange('personal_info', 'had_massage_before', e.target.value)}
      >
        <option value="">Select</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
      {errors.had_massage_before && <p className="error">{errors.had_massage_before}</p>}
    </div>

    <div className="form-group-intake">
      <label className="intake-label-inline">
        Reason for Massage<span className="mandatory-asterisk">*</span>
      </label>
      <input
        type="text"
        placeholder="Reason for massage"
        className="input-intake-form"
        value={medicalHistory?.personal_info?.massage_reason || ''}
        onChange={(e) => handleChange('personal_info', 'massage_reason', e.target.value)}
      />
      {errors.massage_reason && <p className="error">{errors.massage_reason}</p>}
    </div>

    <div className="form-group-intake">
      <textarea
        placeholder="Current Medications"
        className="textarea-intake-form"
        value={medicalHistory?.personal_info?.current_medications || ''}
        onChange={(e) => handleChange('personal_info', 'current_medications', e.target.value)}
      />
    </div>

    <div className="form-group-intake">
      <textarea
        placeholder="Previous Illnesses or Surgeries"
        className="textarea-intake-form"
        value={medicalHistory?.personal_info?.previous_illnesses || ''}
        onChange={(e) => handleChange('personal_info', 'previous_illnesses', e.target.value)}
      />
    </div>

    <div className="form-group-intake">
      <textarea
        placeholder="Accidents (with dates)"
        className="textarea-intake-form"
        value={medicalHistory?.personal_info?.accidents || ''}
        onChange={(e) => handleChange('personal_info', 'accidents', e.target.value)}
      />
    </div>

    <div className="form-group-intake">
      <textarea
        placeholder="Other Medical Conditions"
        className="textarea-intake-form"
        value={medicalHistory?.personal_info?.other_conditions || ''}
        onChange={(e) => handleChange('personal_info', 'other_conditions', e.target.value)}
      />
    </div>

    <div className="form-group-intake">
      <textarea
        placeholder="Family History (illnesses, surgeries, etc.)"
        className="textarea-intake-form"
        value={medicalHistory?.personal_info?.family_history || ''}
        onChange={(e) => handleChange('personal_info', 'family_history', e.target.value)}
      />
    </div>
    </fieldset>


    <fieldset className="intake-section-intake-form joint-soft-tissue-section-intake-form">
    <legend className="intake-legend">Joint / Soft Tissue Discomfort</legend>
    <LegendHorizontal/>
    <div className="form-group-intake radio-grid-intake-form">
        {
        [
            'arms', 'upper_back', 'mid_back', 'lower_back', 'degenerative_discs',
            'feet', 'hands', 'hips', 'jaw', 'knees', 'legs', 'neck',
            'osteo_arthritis', 'rheumatoid_arthritis', 'sciatica', 'shoulders'
        ].map(field => (
            <div key={field} className="radio-group-intake-form">
            <label className="radio-label-intake-form">
                {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </label>
            {['none', 'past', 'current'].map(option => (
                <label key={option} className="radio-option-intake-form">
                <input
                    type="radio"
                    name={`joint_soft_tissue-${field}`}
                    value={option}
                    checked={medicalHistory.joint_soft_tissue?.[field] === option}
                    onChange={() => handleChange('joint_soft_tissue', field, option)}
                />
                {option.charAt(0).toUpperCase()}
                </label>
            ))}
            </div>
        ))}
    </div>

    <div className="form-group-intake two-column-intake">
        <input
        type="text"
        className="input-intake-form"
        placeholder="Limitation of Movement (which joints)"
        value={medicalHistory.joint_soft_tissue?.limitation_of_movement || ''}
        onChange={(e) =>
            handleChange('joint_soft_tissue', 'limitation_of_movement', e.target.value)
        }
        />
        <input
        type="text"
        className="input-intake-form"
        placeholder="Other (specify)"
        value={medicalHistory.joint_soft_tissue?.other || ''}
        onChange={(e) => handleChange('joint_soft_tissue', 'other', e.target.value)}
        />
    </div>

    <div className="form-group-intake">
        <textarea
        placeholder="Notes"
        className="textarea-intake-form"
        value={medicalHistory.joint_soft_tissue?.joint_notes || ''}
        onChange={(e) => handleChange('joint_soft_tissue', 'joint_notes', e.target.value)}
        />
    </div>
    </fieldset>

    <fieldset className="intake-section-intake-form skin-section-intake-form">
    <legend className="intake-legend">Skin</legend>
    <LegendHorizontal/>
    <div className="form-group-intake radio-grid-intake-form">
        {
        ['rashes', 'itching', 'bruise_easily', 'dryness', 'boils'].map(field => (
            <div key={field} className="radio-group-intake-form">
            <label className="radio-label-intake-form">
                {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </label>
            {['none', 'past', 'current'].map(option => (
                <label key={option} className="radio-option-intake-form">
                <input
                    type="radio"
                    name={`skin-${field}`}
                    value={option}
                    checked={medicalHistory.skin?.[field] === option}
                    onChange={() => handleChange('skin', field, option)}
                />
                {option.charAt(0).toUpperCase()}
                </label>
            ))}
            </div>
        ))}
    </div>

    <div className="form-group-intake">
        <input
        type="text"
        className="input-intake-form"
        placeholder="Other (specify)"
        value={medicalHistory.skin?.other || ''}
        onChange={(e) => handleChange('skin', 'other', e.target.value)}
        />
    </div>
    </fieldset>


    <fieldset className="intake-section-intake-form general-symptoms-section-intake-form">
    <legend className="intake-legend">General Symptoms</legend>
    <LegendHorizontal/>
    <div className="form-group-intake radio-grid-intake-form">
        {
        [
            'fainting', 'dizziness', 'loss_of_sleep', 'fatigue', 'nervousness',
            'weight_change', 'numbness', 'tingling', 'paralysis',
            'tension_headaches', 'migraines'
        ].map(field => (
            <div key={field} className="radio-group-intake-form">
            <label className="radio-label-intake-form">
                {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </label>
            {['none', 'past', 'current'].map(option => (
                <label key={option} className="radio-option-intake-form">
                <input
                    type="radio"
                    name={`general_symptoms-${field}`}
                    value={option}
                    checked={medicalHistory.general_symptoms?.[field] === option}
                    onChange={() => handleChange('general_symptoms', field, option)}
                />
                {option.charAt(0).toUpperCase()}
                </label>
            ))}
            </div>
        ))}
    </div>
    </fieldset>


    <fieldset className="intake-section-intake-form cardiovascular-section-intake-form">
    <legend className="intake-legend">Cardiovascular</legend>
    <LegendHorizontal/>
    <div className="form-group-intake radio-grid-intake-form">
        {
        [
            'high_bp', 'low_bp', 'coronary_disease', 'heart_attack', 'phlebitis',
            'stroke', 'pacemaker', 'heart_murmur', 'palpitations',
            'varicose_veins', 'ankle_swelling', 'poor_circulation'
        ].map(field => (
            <div key={field} className="radio-group-intake-form">
            <label className="radio-label-intake-form">
                {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </label>
            {['none', 'past', 'current'].map(option => (
                <label key={option} className="radio-option-intake-form">
                <input
                    type="radio"
                    name={`cardiovascular-${field}`}
                    value={option}
                    checked={medicalHistory.cardiovascular?.[field] === option}
                    onChange={() => handleChange('cardiovascular', field, option)}
                />
                {option.charAt(0).toUpperCase()}
                </label>
            ))}
            </div>
        ))}
    </div>
    </fieldset>

    <fieldset className="intake-section-intake-form infectious-section-intake-form">
    <legend className="intake-legend">Infectious Conditions</legend>
    <LegendHorizontal/>
    <div className="form-group-intake radio-grid-intake-form">
        {
        [
            'hepatitis', 'tuberculosis', 'hiv', 'herpes',
            'cold', 'flu', 'athletes_foot', 'warts'
        ].map(field => (
            <div key={field} className="radio-group-intake-form">
            <label className="radio-label-intake-form">
                {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </label>
            {['none', 'past', 'current'].map(option => (
                <label key={option} className="radio-option-intake-form">
                <input
                    type="radio"
                    name={`infectious-${field}`}
                    value={option}
                    checked={medicalHistory.infectious?.[field] === option}
                    onChange={() => handleChange('infectious', field, option)}
                />
                {option.charAt(0).toUpperCase()}
                </label>
            ))}
            </div>
        ))}
    </div>

    <div className="form-group-intake">
        <input
        type="text"
        className="input-intake-form"
        placeholder="Other (specify)"
        value={medicalHistory.infectious?.other || ''}
        onChange={(e) => handleChange('infectious', 'other', e.target.value)}
        />
    </div>
    </fieldset>


    <fieldset className="intake-section-intake-form digestive-section-intake-form">
        <legend className="intake-legend">Digestive System</legend>
        <LegendHorizontal/>
        <div className="form-group-intake radio-grid-intake-form">
            {
            [
                'poor_appetite', 'gas', 'constipation', 'diarrhea',
                'nausea', 'ulcer', 'vomiting'
            ].map(field => (
                <div key={field} className="radio-group-intake-form">
                <label className="radio-label-intake-form">
                    {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </label>
                {['none', 'past', 'current'].map(option => (
                    <label key={option} className="radio-option-intake-form">
                    <input
                        type="radio"
                        name={`digestive-${field}`}
                        value={option}
                        checked={medicalHistory.digestive?.[field] === option}
                        onChange={() => handleChange('digestive', field, option)}
                    />
                    {option.charAt(0).toUpperCase()}
                    </label>
                ))}
                </div>
            ))}
        </div>
    </fieldset>


    <fieldset className="intake-section-intake-form eent-section-intake-form">
    <legend className="intake-legend">Eye, Ear, Nose, Throat (EENT)</legend>
    <LegendHorizontal/>
    <div className="form-group-intake radio-grid-intake-form">
        {
        [
            'allergies', 'frequent_colds', 'glasses_contacts',
            'hearing_aid', 'hearing_loss', 'sinus_infection', 'swollen_glands'
        ].map(field => (
            <div key={field} className="radio-group-intake-form">
            <label className="radio-label-intake-form">
                {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </label>
            {['none', 'past', 'current'].map(option => (
                <label key={option} className="radio-option-intake-form">
                <input
                    type="radio"
                    name={`eent-${field}`}
                    value={option}
                    checked={medicalHistory.eent?.[field] === option}
                    onChange={() => handleChange('eent', field, option)}
                />
                {option.charAt(0).toUpperCase()}
                </label>
            ))}
            </div>
        ))}
    </div>
    </fieldset>


    <fieldset className="intake-section-intake-form reproductive-section-intake-form">
    <legend className="intake-legend">Reproductive System</legend>
    <LegendHorizontal/>
    <div className="form-group-intake radio-grid-intake-form">
        {[
        'pregnant', 'painful_menstruation', 'heavy_flow', 'irregular_cycle',
        'swollen_breasts', 'menopausal', 'pre_menopausal',
        'post_menopausal', 'birth_control'
        ].map(field => (
        <div key={field} className="radio-group-intake-form">
            <label className="radio-label-intake-form">
            {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </label>
            {['none', 'past', 'current'].map(option => (
            <label key={option} className="radio-option-intake-form">
                <input
                type="radio"
                name={`reproductive-${field}`}
                value={option}
                checked={medicalHistory.reproductive?.[field] === option}
                onChange={() => handleChange('reproductive', field, option)}
                />
                {option.charAt(0).toUpperCase()}
            </label>
            ))}
        </div>
        ))}
    </div>

    <div className="form-group-intake two-column-intake">
        <div className="date-field-wrapper-intake">
        <label className="date-inline-label-intake">Due Date</label>
        <input
            type="date"
            className="input-intake-form"
            value={medicalHistory.reproductive?.due_date || ''}
            onChange={(e) => handleChange('reproductive', 'due_date', e.target.value)}
        />
        </div>

        <input
        type="text"
        placeholder="Birth Control Type"
        className="input-intake-form"
        value={medicalHistory.reproductive?.birth_control_type || ''}
        onChange={(e) => handleChange('reproductive', 'birth_control_type', e.target.value)}
        />
    </div>
    </fieldset>


    <fieldset className="intake-section-intake-form respiratory-section-intake-form">
    <legend className="intake-legend">Respiratory System</legend>
    <LegendHorizontal/>
    <div className="form-group-intake radio-grid-intake-form">
        {[
        'chronic_cough', 'bronchitis', 'asthma', 'hay_fever',
        'difficulty_breathing', 'smoking', 'emphysema', 'pneumonia'
        ].map(field => (
        <div key={field} className="radio-group-intake-form">
            <label className="radio-label-intake-form">
            {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </label>
            {['none', 'past', 'current'].map(option => (
            <label key={option} className="radio-option-intake-form">
                <input
                type="radio"
                name={`respiratory-${field}`}
                value={option}
                checked={medicalHistory.respiratory?.[field] === option}
                onChange={() => handleChange('respiratory', field, option)}
                />
                {option.charAt(0).toUpperCase()}
            </label>
            ))}
        </div>
        ))}
    </div>
    </fieldset>


    <fieldset className="intake-section-intake-form lifestyle-section-intake-form">
    <legend className="intake-legend">Lifestyle</legend>
    <LegendHorizontal/>
    <div className="form-group-intake two-column-intake">
        <input
        type="text"
        placeholder="Eating Habits"
        className="input-intake-form"
        value={medicalHistory.lifestyle?.eating_habits || ''}
        onChange={(e) => handleChange('lifestyle', 'eating_habits', e.target.value)}
        />
        <input
        type="text"
        placeholder="Takes Vitamins?"
        className="input-intake-form"
        value={medicalHistory.lifestyle?.takes_vitamins || ''}
        onChange={(e) => handleChange('lifestyle', 'takes_vitamins', e.target.value)}
        />
    </div>

    <div className="form-group-intake two-column-intake">
        <input
        type="text"
        placeholder="Vitamin Type"
        className="input-intake-form"
        value={medicalHistory.lifestyle?.vitamin_type || ''}
        onChange={(e) => handleChange('lifestyle', 'vitamin_type', e.target.value)}
        />
        <input
        type="text"
        placeholder="Vitamin Frequency"
        className="input-intake-form"
        value={medicalHistory.lifestyle?.vitamin_frequency || ''}
        onChange={(e) => handleChange('lifestyle', 'vitamin_frequency', e.target.value)}
        />
    </div>

    <div className="form-group-intake two-column-intake">
        <input
        type="text"
        placeholder="Regular Exercise?"
        className="input-intake-form"
        value={medicalHistory.lifestyle?.regular_exercise || ''}
        onChange={(e) => handleChange('lifestyle', 'regular_exercise', e.target.value)}
        />
        <input
        type="text"
        placeholder="Exercise Type"
        className="input-intake-form"
        value={medicalHistory.lifestyle?.exercise_type || ''}
        onChange={(e) => handleChange('lifestyle', 'exercise_type', e.target.value)}
        />
    </div>

    <div className="form-group-intake two-column-intake">
        <input
        type="text"
        placeholder="Exercise Frequency"
        className="input-intake-form"
        value={medicalHistory.lifestyle?.exercise_frequency || ''}
        onChange={(e) => handleChange('lifestyle', 'exercise_frequency', e.target.value)}
        />
        <input
        type="text"
        placeholder="Energy Level"
        className="input-intake-form"
        value={medicalHistory.lifestyle?.energy_level || ''}
        onChange={(e) => handleChange('lifestyle', 'energy_level', e.target.value)}
        />
    </div>

    <div className="form-group-intake two-column-intake">
        <input
        type="text"
        placeholder="Stress"
        className="input-intake-form"
        value={medicalHistory.lifestyle?.stress || ''}
        onChange={(e) => handleChange('lifestyle', 'stress', e.target.value)}
        />
        <input
        type="text"
        placeholder="Stress Type"
        className="input-intake-form"
        value={medicalHistory.lifestyle?.stress_type || ''}
        onChange={(e) => handleChange('lifestyle', 'stress_type', e.target.value)}
        />
    </div>

    <div className="form-group-intake two-column-intake">
        <input
        type="text"
        placeholder="Computer Use"
        className="input-intake-form"
        value={medicalHistory.lifestyle?.computer_use || ''}
        onChange={(e) => handleChange('lifestyle', 'computer_use', e.target.value)}
        />
        <input
        type="text"
        placeholder="Hours per Day"
        className="input-intake-form"
        value={medicalHistory.lifestyle?.hours_per_day || ''}
        onChange={(e) => handleChange('lifestyle', 'hours_per_day', e.target.value)}
        />
    </div>
    </fieldset>

    <fieldset className="intake-section-intake-form consent-section-intake-form">
    <legend className="intake-legend">Consent</legend>
    <div className="form-group-intake">
    <label className="intake-label-inline">
      Signature<span className="mandatory-asterisk">*</span>
    </label>

        <input
        type="text"
        placeholder="Signature"
        className="input-intake-form signature-input-intake"
        value={medicalHistory.consent?.signature || ''}
        onChange={(e) => handleChange('consent', 'signature', e.target.value)}
        />
        {errors.signature && <p className="error">{errors.signature}</p>}
    </div>

    <div className="form-group-intake">
    <label className="intake-label-inline">
      Date<span className="mandatory-asterisk">*</span>
    </label>

        <input
        type="date"
        placeholder="Date"
        className="input-intake-form"
        value={medicalHistory.consent?.consent_date || ''}
        onChange={(e) => handleChange('consent', 'consent_date', e.target.value)}
        />
        {errors.consent_date && <p className="error">{errors.consent_date}</p>}
    </div>
    </fieldset>


     <button type="submit" className="fancy-btn">Submit</button>
     
    </form>
    ) : (
      <div className="intake-no-appointment-container">
      <div className="intake-info-card">
      <h2 className="intake-info-heading">Your Health Journey Begins Here</h2>
        <p className="intake-info-text">
          To help us better understand your health needs, you’ll be able to fill out your intake form once you've scheduled at least one appointment with our clinic.
        </p>
        <p className="intake-info-text">
          Please visit the <strong>Schedule</strong> tab to book an appointment. Our team is looking forward to welcoming you and supporting your care journey.
        </p>
        <p className="intake-info-text">
          If you need any assistance with booking, feel free to reach out to our front desk. We're here to help every step of the way.
        </p>
      </div>
    </div>


    )
    
  );
};

export default PatientIntakeForm;
